import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Calendar, MapPin, Clock, Ticket, Play, Check, Gift, Sparkles, ArrowRight, Users } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BASE_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to get full image URL
const getImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  if (url.startsWith('/uploads')) return `${BASE_URL}/api${url}`;
  return url;
};

const EventLandingPage = () => {
  const { eventId } = useParams();
  const [event, setEvent] = useState(null);
  const [siteSettings, setSiteSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showVideo, setShowVideo] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [eventRes, settingsRes] = await Promise.all([
          axios.get(`${API}/events/${eventId}`),
          axios.get(`${API}/site-settings`)
        ]);
        setEvent(eventRes.data);
        setSiteSettings(settingsRes.data);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [eventId]);

  // Get ticket status colors from CMS
  const ticketColors = siteSettings?.ticket_status_colors || {
    available: '#22C55E',
    limited: '#F59E0B',
    sold_out: '#EF4444',
    coming_soon: '#3B82F6'
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'sold_out': return ticketColors.sold_out;
      case 'limited': return ticketColors.limited;
      case 'coming_soon': return ticketColors.coming_soon;
      default: return ticketColors.available;
    }
  };

  const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0 }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    // Parse date as local time to avoid timezone shift
    const [year, month, day] = dateStr.split('-').map(Number);
    const date = new Date(year, month - 1, day); // month is 0-indexed
    return date.toLocaleDateString('en-US', { 
      weekday: 'long',
      month: 'long', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <main className="pt-20 min-h-screen flex items-center justify-center">
        <div className="spinner" />
      </main>
    );
  }

  if (!event) {
    return (
      <main className="pt-20 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Calendar className="w-16 h-16 text-[#D4AF37] mx-auto mb-6" />
          <h3 className="font-['Playfair_Display'] text-2xl font-semibold text-[#F5F5F0] mb-4">
            Event Not Found
          </h3>
          <p className="text-[#A1A1AA] mb-8">This event may have been removed or doesn't exist.</p>
          <Link to="/events" className="btn-primary">View All Events</Link>
        </div>
      </main>
    );
  }

  return (
    <main className="pt-20" data-testid="event-landing-page">
      {/* Hero with Banner */}
      <section className="relative min-h-[80vh] flex items-center" data-testid="event-hero">
        <div className="absolute inset-0">
          <img
            src={getImageUrl(event.banner_image) || "https://images.unsplash.com/photo-1551024709-8f23befc6f87?w=1920"}
            alt={event.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-[#050505]/90 to-[#050505]/60" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent" />
        </div>
        
        <div className="container-luxury relative z-10 py-20">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeInUp}
            transition={{ duration: 0.8 }}
            className="max-w-3xl"
          >
            {event.category && (
              <motion.span 
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 }}
                className="inline-block bg-[#D4AF37] text-[#050505] px-5 py-1.5 text-xs uppercase tracking-[0.2em] font-semibold mb-6"
              >
                {event.category}
              </motion.span>
            )}
            <h1 
              className="text-5xl sm:text-6xl lg:text-7xl font-['Playfair_Display'] font-bold mb-4 leading-tight"
              style={{ color: event.title_color || '#F5F5F0' }}
            >
              {event.title}
            </h1>
            {event.subtitle && (
              <motion.p 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.4 }}
                className="text-xl mb-3 font-medium"
                style={{ color: event.subtitle_color || '#D4AF37' }}
              >
                {event.subtitle}
              </motion.p>
            )}
            {event.tagline && (
              <motion.p 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
                className="text-[#F5F5F0] text-2xl font-['Cormorant_Garamond'] italic mb-8 opacity-90"
              >
                "{event.tagline}"
              </motion.p>
            )}
            
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="flex flex-wrap gap-8 mb-10"
            >
              <div className="flex items-center gap-3 text-[#F5F5F0]">
                <div className="w-12 h-12 border border-[#D4AF37] flex items-center justify-center">
                  <Calendar className="w-5 h-5 text-[#D4AF37]" />
                </div>
                <div>
                  <p className="text-xs text-[#D4AF37] uppercase tracking-widest">Date</p>
                  <span className="text-lg">{formatDate(event.date)}</span>
                </div>
              </div>
              <div className="flex items-center gap-3 text-[#F5F5F0]">
                <div className="w-12 h-12 border border-[#D4AF37] flex items-center justify-center">
                  <Clock className="w-5 h-5 text-[#D4AF37]" />
                </div>
                <div>
                  <p className="text-xs text-[#D4AF37] uppercase tracking-widest">Time</p>
                  <span className="text-lg">{event.time}</span>
                </div>
              </div>
            </motion.div>
            
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7 }}
              className="flex items-start gap-3 mb-10"
            >
              <div className="w-12 h-12 border border-[#D4AF37] flex items-center justify-center flex-shrink-0">
                <MapPin className="w-5 h-5 text-[#D4AF37]" />
              </div>
              <div>
                <p className="text-xs text-[#D4AF37] uppercase tracking-widest">Location</p>
                <p className="text-[#F5F5F0] text-lg">{event.location}</p>
                {event.address && <p className="text-[#A1A1AA]">{event.address}</p>}
              </div>
            </motion.div>

            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8 }}
              className="flex flex-wrap gap-4 items-center"
            >
              <Link 
                to={`/events/${event.id}/register`}
                className="px-8 py-4 text-lg font-semibold transition-all inline-flex items-center gap-3 hover:scale-105"
                style={{ 
                  backgroundColor: event.button_color || '#D4AF37',
                  color: '#050505'
                }}
                data-testid="book-tickets-btn"
              >
                <Ticket className="w-5 h-5" />
                {event.button_text || 'Book Tickets'}
              </Link>
              {event.video_url && (
                <button
                  onClick={() => setShowVideo(true)}
                  className="px-6 py-4 border-2 border-[#F5F5F0] text-[#F5F5F0] hover:bg-[#F5F5F0] hover:text-[#050505] transition-colors inline-flex items-center gap-2"
                  data-testid="watch-video-btn"
                >
                  <Play className="w-5 h-5" />
                  Watch Video
                </button>
              )}
            </motion.div>
            {event.cta_text && (
              <motion.p 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1 }}
                className="text-[#D4AF37] mt-4 text-sm"
              >
                {event.cta_text}
              </motion.p>
            )}
          </motion.div>
        </div>
        
        {/* Scroll indicator */}
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2 }}
          className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
        >
          <div className="w-6 h-10 border-2 border-[#D4AF37]/50 rounded-full flex justify-center pt-2">
            <motion.div 
              animate={{ y: [0, 8, 0] }}
              transition={{ repeat: Infinity, duration: 1.5 }}
              className="w-1.5 h-1.5 bg-[#D4AF37] rounded-full"
            />
          </div>
        </motion.div>
      </section>

      {/* Video Modal */}
      {showVideo && event.video_url && (
        <div 
          className="fixed inset-0 z-50 bg-[#050505]/95 flex items-center justify-center p-4"
          onClick={() => setShowVideo(false)}
        >
          <div className="max-w-4xl w-full" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setShowVideo(false)}
              className="absolute top-4 right-4 text-[#D4AF37] hover:text-[#F5F5F0] text-sm uppercase tracking-widest"
            >
              Close
            </button>
            <video
              src={getImageUrl(event.video_url)}
              controls
              autoPlay
              className="w-full aspect-video"
              data-testid="event-video"
            >
              Your browser does not support the video tag.
            </video>
          </div>
        </div>
      )}

      {/* Overview Section */}
      <section className="section-padding bg-[#050505]" data-testid="event-overview">
        <div className="container-luxury">
          <div className="grid lg:grid-cols-2 gap-16">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUp}
              transition={{ duration: 0.6 }}
            >
              <p className="text-[#D4AF37] uppercase tracking-[0.2em] text-sm mb-4">
                About This Event
              </p>
              <h2 className="text-3xl sm:text-4xl font-['Playfair_Display'] font-bold mb-6 text-[#F5F5F0]">
                Event Overview
              </h2>
              <div className="gold-divider" />
              
              {/* Rich Text Description */}
              {event.description ? (
                <div 
                  className="prose prose-invert max-w-none mb-6 event-description"
                  dangerouslySetInnerHTML={{ __html: event.description }}
                />
              ) : (
                <p className="text-[#A1A1AA] leading-relaxed mb-6">
                  {event.overview}
                </p>
              )}
              
              {event.highlights && event.highlights.length > 0 && (
                <div className="space-y-4">
                  <h4 className="text-[#D4AF37] text-sm uppercase tracking-widest">Highlights</h4>
                  <ul className="space-y-3">
                    {event.highlights.map((highlight, i) => (
                      <li key={i} className="flex items-start gap-3 text-[#A1A1AA]">
                        <Check className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                        <span>{highlight}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="space-y-8"
            >
              {/* Event Details Card */}
              <div className="luxury-card p-8">
                <h3 className="font-['Playfair_Display'] text-xl font-semibold text-[#F5F5F0] mb-6">
                  Event Details
                </h3>
                <div className="space-y-4">
                  <div className="flex items-start gap-4">
                    <Calendar className="w-5 h-5 text-[#D4AF37] mt-0.5" />
                    <div>
                      <p className="text-[#F5F5F0] font-medium">Date & Time</p>
                      <p className="text-[#A1A1AA] text-sm">{formatDate(event.date)}</p>
                      <p className="text-[#A1A1AA] text-sm">{event.time}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <MapPin className="w-5 h-5 text-[#D4AF37] mt-0.5" />
                    <div>
                      <p className="text-[#F5F5F0] font-medium">Location</p>
                      <p className="text-[#A1A1AA] text-sm">{event.location}</p>
                      {event.address && <p className="text-[#A1A1AA] text-sm">{event.address}</p>}
                    </div>
                  </div>
                  {event.capacity && (
                    <div className="flex items-start gap-4">
                      <Users className="w-5 h-5 text-[#D4AF37] mt-0.5" />
                      <div>
                        <p className="text-[#F5F5F0] font-medium">Capacity</p>
                        <p className="text-[#A1A1AA] text-sm">{event.capacity} guests</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* What's Included */}
              {event.included && event.included.length > 0 && (
                <div className="luxury-card p-8">
                  <h3 className="font-['Playfair_Display'] text-xl font-semibold text-[#F5F5F0] mb-6 flex items-center gap-2">
                    <Gift className="w-5 h-5 text-[#D4AF37]" />
                    What's Included
                  </h3>
                  <ul className="space-y-3">
                    {event.included.map((item, i) => (
                      <li key={i} className="flex items-start gap-3 text-[#A1A1AA]">
                        <Sparkles className="w-4 h-4 text-[#D4AF37] flex-shrink-0 mt-1" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Tickets Section */}
      {event.ticket_options && event.ticket_options.length > 0 && (
        <section className="section-padding bg-[#0F0F0F] border-t border-[rgba(212,175,55,0.15)]" data-testid="tickets-section">
          <div className="container-luxury">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUp}
              transition={{ duration: 0.6 }}
              className="text-center mb-16"
            >
              <p className="text-[#D4AF37] uppercase tracking-[0.2em] text-sm mb-4">
                Secure Your Spot
              </p>
              <h2 className="text-3xl sm:text-4xl font-['Playfair_Display'] font-bold mb-4 text-[#F5F5F0]">
                Choose Your Tickets
              </h2>
              <div className="gold-divider mx-auto" />
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-5xl mx-auto">
              {event.ticket_options.map((ticket, index) => (
                <motion.div
                  key={ticket.name}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className={`luxury-card p-8 cursor-pointer transition-all ${
                    selectedTicket === ticket.name 
                      ? 'border-[#D4AF37] border-2' 
                      : 'hover:border-[rgba(212,175,55,0.5)]'
                  } ${ticket.status === 'sold_out' ? 'opacity-60' : ''}`}
                  onClick={() => ticket.status !== 'sold_out' && setSelectedTicket(ticket.name)}
                  data-testid={`ticket-${ticket.name}`}
                >
                  <div className="text-center">
                    <h3 className="font-['Playfair_Display'] text-xl font-semibold text-[#F5F5F0] mb-2">
                      {ticket.name}
                    </h3>
                    <p className="text-3xl font-['Playfair_Display'] font-bold gold-text mb-4">
                      C${ticket.price}
                    </p>
                    {ticket.description && (
                      <p className="text-[#A1A1AA] text-sm mb-4">{ticket.description}</p>
                    )}
                    {ticket.perks && ticket.perks.length > 0 && (
                      <ul className="text-left space-y-2 mb-6">
                        {ticket.perks.map((perk, i) => (
                          <li key={i} className="flex items-start gap-2 text-[#A1A1AA] text-sm">
                            <Check className="w-4 h-4 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                            <span>{perk}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                    {ticket.status === 'sold_out' ? (
                      <span 
                        className="inline-block px-4 py-2 text-white text-sm uppercase tracking-widest font-medium"
                        style={{ backgroundColor: getStatusColor('sold_out') }}
                      >
                        Sold Out
                      </span>
                    ) : ticket.status === 'coming_soon' ? (
                      <span 
                        className="inline-block px-4 py-2 text-white text-sm uppercase tracking-widest font-medium"
                        style={{ backgroundColor: getStatusColor('coming_soon') }}
                      >
                        Coming Soon
                      </span>
                    ) : ticket.status === 'limited' ? (
                      <span 
                        className="inline-block px-4 py-2 text-white text-sm uppercase tracking-widest font-medium"
                        style={{ backgroundColor: getStatusColor('limited') }}
                      >
                        Limited
                      </span>
                    ) : (
                      <span 
                        className="inline-block px-4 py-2 text-white text-sm font-medium"
                        style={{ backgroundColor: getStatusColor('available') }}
                      >
                        {ticket.available || 'Available'}
                      </span>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Check if any tickets are available */}
            {(() => {
              const hasAvailableTickets = event.ticket_options?.some(t => t.status !== 'sold_out' && t.status !== 'coming_soon');
              return (
                <motion.div
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                  className="text-center mt-12"
                >
                  {hasAvailableTickets ? (
                    <>
                      <Link
                        to={`/events/${event.id}/register`}
                        className="btn-primary inline-flex items-center gap-2 text-lg px-10 py-4"
                        data-testid="book-now-btn"
                      >
                        <Ticket className="w-5 h-5" />
                        Book Now
                      </Link>
                      <p className="text-[#52525B] text-sm mt-4">
                        Questions? <Link to="/contact" className="text-[#D4AF37] hover:underline">Contact us</Link>
                      </p>
                    </>
                  ) : (
                    <>
                      <div className="inline-flex items-center gap-2 text-lg px-10 py-4 bg-[#52525B]/20 text-[#52525B] cursor-not-allowed border border-[#52525B]/30">
                        <Ticket className="w-5 h-5" />
                        Sold Out
                      </div>
                      <p className="text-[#52525B] text-sm mt-4">
                        All tickets sold out. <Link to="/contact" className="text-[#D4AF37] hover:underline">Join waitlist</Link>
                      </p>
                    </>
                  )}
                </motion.div>
              );
            })()}
          </div>
        </section>
      )}

      {/* Host Your Own Section */}
      <section className="section-padding bg-[#050505] border-t border-[rgba(212,175,55,0.15)]" data-testid="host-section">
        <div className="container-luxury">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="aspect-[4/3] overflow-hidden"
            >
              <img
                src="https://images.unsplash.com/photo-1712314947762-039576e58bc6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHwxfHxlbGVnYW50JTIwZXZlbnQlMjBzZXR1cCUyMHdlZGRpbmclMjByZWNlcHRpb24lMjBuaWdodHxlbnwwfHx8fDE3NzIzNTM1ODV8MA&ixlib=rb-4.1.0&q=85"
                alt="Host your own event"
                className="w-full h-full object-cover"
              />
            </motion.div>

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUp}
              transition={{ duration: 0.6 }}
            >
              <p className="text-[#D4AF37] uppercase tracking-[0.2em] text-sm mb-4">
                Private Events
              </p>
              <h2 className="text-3xl sm:text-4xl font-['Playfair_Display'] font-bold mb-6 text-[#F5F5F0]">
                Host Your Own Event
              </h2>
              <div className="gold-divider" />
              <p className="text-[#A1A1AA] leading-relaxed mb-6">
                Want to bring the Mix & Mingle experience to your own celebration? 
                From weddings to corporate galas, we offer full-service beverage 
                catering that transforms any event into an unforgettable experience.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link 
                  to="/experiences" 
                  className="btn-primary inline-flex items-center justify-center gap-2"
                  data-testid="view-experiences-btn"
                >
                  View Experiences
                  <ArrowRight className="w-5 h-5" />
                </Link>
                <Link 
                  to="/contact" 
                  className="btn-secondary text-center"
                  data-testid="contact-events-btn"
                >
                  Contact Us
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default EventLandingPage;
